import * as React from "react";
import moment from "moment-timezone";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { IACLEmail } from "intake_shared/src/types";
import {
    AssignmentEvent,
    AssignmentMode,
    assignmentWeeklyHours,
    isActiveCareCoordinator,
    canAutoAssignToCoordinator,
} from "intake_shared/src/assignment";
import {
    getAssignmentConfiguration,
    getAssignmentEvents,
    getCoordinatorTimeOffForBalance,
    setAssignmentMode,
    TimeOffRange,
} from "../../../store/assignment";
import { Button } from "../bits/Button/Button";
import { Card } from "../bits/Card/Card";
import { Select } from "../bits/Select/Select";
import Table from "../bits/Table/Table";
import { Badge } from "../bits/Badge/Badge";
import Alert from "../bits/Alert/Alert";
import EmptyListState from "../bits/EmptyListState/EmptyListState";
import { Dialog } from "../bits/Dialog/Dialog";
import RefreshIcon from "../icons/Refresh";
import { InfoIcon } from "../icons/Info";
import { assignmentEventDate, timeOffDays } from "./assignmentUtils";
import styles from "./Assignment.module.scss";

function coordinatorEmail(value: unknown): string {
    return typeof value === "string" ? value.trim().toLowerCase() : "";
}

function AssignmentHelp({
    id,
    label,
    children,
}: {
    id: string;
    label: string;
    children: React.ReactNode;
}) {
    const [show, setShow] = React.useState(false);
    return (
        <OverlayTrigger
            trigger="click"
            placement="auto"
            rootClose
            show={show}
            onToggle={setShow}
            overlay={
                <Popover id={id} className={styles.helpPopover}>
                    <Popover.Title as="h3">{label}</Popover.Title>
                    <Popover.Content>{children}</Popover.Content>
                </Popover>
            }
        >
            <Button
                variant="normal"
                size="auto"
                className={styles.helpButton}
                aria-label={`About ${label.toLowerCase()}`}
                aria-expanded={show}
                aria-controls={show ? id : undefined}
                aria-describedby={show ? id : undefined}
                onKeyDown={(event) => {
                    if (show && event.key === "Escape") {
                        event.preventDefault();
                        event.stopPropagation();
                        setShow(false);
                    }
                }}
            >
                <span aria-hidden="true">
                    <InfoIcon />
                </span>
            </Button>
        </OverlayTrigger>
    );
}

function columnHeading(label: string, id: string, help: string) {
    return (
        <span className={styles.columnHeading}>
            {label}
            <AssignmentHelp id={id} label={label}>
                {help}
            </AssignmentHelp>
        </span>
    );
}

export default function AssignmentBalance({
    acls,
    refreshKey,
    onRefresh,
    onManageCoordinator,
    managementDisabled = false,
    refreshing = false,
    rosterUnavailable = false,
    timeOffUpdate,
}: {
    acls: IACLEmail[];
    refreshKey: number;
    onRefresh?: () => void;
    onManageCoordinator?: (coordinator: IACLEmail) => void;
    managementDisabled?: boolean;
    refreshing?: boolean;
    rosterUnavailable?: boolean;
    timeOffUpdate?: { email: string; ranges: TimeOffRange[] };
}) {
    const membershipKey = JSON.stringify(
        Array.from(
            new Set(
                acls
                    .filter(isActiveCareCoordinator)
                    .map((acl) => coordinatorEmail(acl.email))
                    .filter(Boolean)
            )
        ).sort()
    );
    const editedRanges = React.useRef<Record<string, TimeOffRange[]>>({});
    const rangeRevisions = React.useRef<Record<string, number>>({});
    React.useEffect(() => {
        if (!timeOffUpdate) return;
        const email = coordinatorEmail(timeOffUpdate.email);
        if (!email) return;
        editedRanges.current[email] = timeOffUpdate.ranges;
        rangeRevisions.current[email] = (rangeRevisions.current[email] || 0) + 1;
        setTimeOff((previous) => ({ ...previous, [email]: timeOffUpdate.ranges }));
    }, [timeOffUpdate]);
    const [modeSelection, setModeSelection] = React.useState<{
        saved: AssignmentMode | null;
        draft: AssignmentMode | null;
    }>({ saved: null, draft: null });
    const mode = modeSelection.saved;
    const draftMode = modeSelection.draft;
    const modeDirty = draftMode !== null && draftMode !== mode;
    const [modeAvailable, setModeAvailable] = React.useState(false);
    const modeSavePending = React.useRef(false);
    const [events, setEvents] = React.useState<AssignmentEvent[]>([]);
    const [timeOff, setTimeOff] = React.useState<Record<string, TimeOffRange[]>>({});
    const [loading, setLoading] = React.useState(true);
    const [hasData, setHasData] = React.useState(false);
    const [modeSaved, setModeSaved] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [showFallbacks, setShowFallbacks] = React.useState(false);
    const reviewButton = React.useRef<HTMLButtonElement>(null);
    const closeReview = () => {
        setShowFallbacks(false);
        requestAnimationFrame(() => {
            (
                reviewButton.current || document.getElementById("coordinator-roster")
            )?.focus({ preventScroll: true });
        });
    };
    const [error, setError] = React.useState("");
    const [modeError, setModeError] = React.useState("");
    const [period, setPeriod] = React.useState({ start: "", end: "" });
    const loadVersion = React.useRef(0);
    const modeWriteVersion = React.useRef(0);
    const load = React.useCallback(async () => {
        const version = ++loadVersion.current;
        const revisionsAtStart = { ...rangeRevisions.current };
        const modeReadVersion = modeWriteVersion.current;
        setLoading(true);
        setError("");
        setModeAvailable(false);
        setModeError("");
        setModeSaved(false);
        const configuration = getAssignmentConfiguration()
            .then((value) => {
                if (
                    version === loadVersion.current &&
                    modeReadVersion === modeWriteVersion.current
                ) {
                    setModeSelection((previous) => ({
                        saved: value.mode,
                        draft:
                            previous.draft !== null &&
                            previous.draft !== previous.saved
                                ? previous.draft
                                : value.mode,
                    }));
                    setModeAvailable(true);
                }
            })
            .catch(() => {
                if (
                    version === loadVersion.current &&
                    modeReadVersion === modeWriteVersion.current
                ) {
                    setModeError("Could not load assignment mode. Try again.");
                }
            });
        try {
            const today = moment.tz("America/New_York").startOf("day");
            const first = today.clone().subtract(29, "days");
            const recentEvents = await getAssignmentEvents(first.toDate());
            const emails = new Set([
                ...(JSON.parse(membershipKey) as string[]),
                ...recentEvents
                    .map((event) => coordinatorEmail(event.assigned_to))
                    .filter(Boolean),
            ]);
            const ranges = await Promise.all(
                Array.from(emails).map(async (email) => ({
                    email,
                    ranges: await getCoordinatorTimeOffForBalance(
                        email,
                        first.format("YYYY-MM-DD")
                    ),
                }))
            );
            const byEmail: Record<string, TimeOffRange[]> = {};
            ranges.forEach((entry) => {
                byEmail[entry.email] = entry.ranges;
            });
            if (version !== loadVersion.current) return;
            setEvents(recentEvents);
            for (const email of Object.keys(rangeRevisions.current)) {
                if (
                    (rangeRevisions.current[email] || 0) !==
                    (revisionsAtStart[email] || 0)
                ) {
                    byEmail[email] = editedRanges.current[email];
                }
            }
            setTimeOff(byEmail);
            setPeriod({
                start: first.format("YYYY-MM-DD"),
                end: today.format("YYYY-MM-DD"),
            });
            setHasData(true);
        } catch {
            if (version !== loadVersion.current) return;
            setError("Could not load assignment balance. Try refreshing.");
        } finally {
            await configuration;
            if (version === loadVersion.current) setLoading(false);
        }
    }, [membershipKey]);
    React.useEffect(() => {
        const version = loadVersion;
        void load();
        return () => {
            version.current++;
        };
    }, [load, refreshKey]);

    const saveMode = async () => {
        if (
            modeSavePending.current ||
            loading ||
            refreshing ||
            managementDisabled ||
            !modeAvailable ||
            mode === null ||
            draftMode === null ||
            !modeDirty
        )
            return;
        const next = draftMode;
        modeSavePending.current = true;
        setSaving(true);
        setModeError("");
        setModeSaved(false);
        try {
            await setAssignmentMode(next);
            modeWriteVersion.current++;
            setModeSelection({ saved: next, draft: next });
            setModeAvailable(true);
            setModeSaved(true);
        } catch {
            setModeError(
                "Could not save assignment mode. Your selection is not saved. Try again."
            );
        } finally {
            modeSavePending.current = false;
            setSaving(false);
        }
    };
    const activeByEmail = new Map<string, IACLEmail>();
    const eligibleByEmail = new Map<string, IACLEmail>();
    for (const acl of acls) {
        const email = coordinatorEmail(acl.email);
        if (!email) continue;
        if (isActiveCareCoordinator(acl) && !activeByEmail.has(email))
            activeByEmail.set(email, acl);
        if (canAutoAssignToCoordinator(acl) && !eligibleByEmail.has(email))
            eligibleByEmail.set(email, acl);
    }
    const hoursTotal = Array.from(eligibleByEmail.values()).reduce(
        (sum, acl) => sum + assignmentWeeklyHours(acl.assignment?.weekly_hours),
        0
    );
    const emails = Array.from(
        new Set([
            ...Array.from(activeByEmail.keys()),
            ...events
                .map((event) => coordinatorEmail(event.assigned_to))
                .filter(Boolean),
        ])
    );
    const fallbackEvents = events
        .filter((event) => event.fallback)
        .sort(
            (left, right) =>
                (assignmentEventDate(right.created_at)?.getTime() || 0) -
                (assignmentEventDate(left.created_at)?.getTime() || 0)
        );
    const percent = (numerator: number, denominator: number) =>
        denominator ? `${((100 * numerator) / denominator).toFixed(1)}%` : "—";

    const refreshBusy = loading || refreshing;
    const refreshBlocked = refreshBusy || saving || managementDisabled;
    const manageBlocked = refreshBlocked || rosterUnavailable || Boolean(error);
    const refresh = () => {
        if (!refreshBlocked) void (onRefresh || load)();
    };
    const rows = emails.map((email) => {
        const acl = eligibleByEmail.get(email) || activeByEmail.get(email);
        const hours = assignmentWeeklyHours(acl?.assignment?.weekly_hours);
        const assigned = events.filter(
            (event) => coordinatorEmail(event.assigned_to) === email
        );
        return {
            email,
            acl,
            hours: acl ? hours : "—",
            eligible: eligibleByEmail.has(email),
            inquiries: assigned.length,
            returning: assigned.filter((event) => event.returning_client).length,
            actual: percent(assigned.length, events.length),
            target: eligibleByEmail.has(email) ? percent(hours, hoursTotal) : "—",
            daysOff: timeOffDays(timeOff[email] || [], period.start, period.end),
        };
    });
    const columns = [
        {
            Header: "Coordinator",
            accessor: "email",
            disableSortBy: true,
            Cell: ({ row }: { row: { original: (typeof rows)[number] } }) => (
                <div className={styles.coordinator}>
                    <span>{row.original.email}</span>
                    {!row.original.acl ? (
                        <Badge tone="neutral" content="Historical assignment" />
                    ) : (
                        !row.original.eligible && (
                            <Badge tone="neutral" content="Not auto-assignable" />
                        )
                    )}
                </div>
            ),
        },
        {
            Header: columnHeading(
                "Hours / week",
                "assignment-hours-help",
                "Weekly hours used for weighted assignment. Defaults to 40 if not set."
            ),
            accessor: "hours",
            disableSortBy: true,
        },
        {
            Header: columnHeading(
                "Inquiries",
                "assignment-inquiries-help",
                "Inquiries assigned during the dates shown, including returning clients. Tracking began November 3, 2026. Earlier assignments are not included."
            ),
            accessor: "inquiries",
            disableSortBy: true,
        },
        {
            Header: columnHeading(
                "Returning",
                "assignment-returning-help",
                "Returning clients who kept this coordinator. Included in the Inquiries count."
            ),
            accessor: "returning",
            disableSortBy: true,
        },
        {
            Header: columnHeading(
                "Actual share",
                "assignment-actual-help",
                "The percentage of all inquiries assigned to this coordinator during the dates shown, including returning clients."
            ),
            accessor: "actual",
            disableSortBy: true,
        },
        {
            Header: columnHeading(
                "Current target",
                "assignment-target-help",
                "Share of new inquiries based on current weekly hours. Does not account for time off or booking availability."
            ),
            accessor: "target",
            disableSortBy: true,
        },
        {
            Header: columnHeading(
                "Days off",
                "assignment-days-off-help",
                "Days off during the dates shown. Each day counts once. Block these dates in SimplyBook separately."
            ),
            accessor: "daysOff",
            disableSortBy: true,
        },
        ...(onManageCoordinator
            ? [
                  {
                      Header: "Settings",
                      id: "manage",
                      disableSortBy: true,
                      Cell: ({
                          row,
                      }: {
                          row: { original: (typeof rows)[number] };
                      }) =>
                          row.original.acl ? (
                              <Button
                                  variant="secondary"
                                  disabled={managementDisabled}
                                  aria-label={`Manage ${row.original.email}`}
                                  data-coordinator-email={row.original.email}
                                  aria-disabled={manageBlocked}
                                  onClick={() => {
                                      if (!manageBlocked)
                                          onManageCoordinator(row.original.acl!);
                                  }}
                              >
                                  Manage
                              </Button>
                          ) : (
                              <span className={styles.helper}>Read-only</span>
                          ),
                  },
              ]
            : []),
    ];
    const modes = [
        { label: "Round robin", value: "round_robin" },
        { label: "Weighted by weekly hours", value: "weighted" },
    ];
    const fallbackRows = fallbackEvents.map((event) => ({
        inquiry: event.client_id,
        coordinator: event.assigned_to,
        assigned: assignmentEventDate(event.created_at)
            ? moment(assignmentEventDate(event.created_at))
                  .tz("America/New_York")
                  .format("MMM D, YYYY h:mm a z")
            : "Time unavailable",
        reason: event.skipped
            .map(
                (skip) =>
                    `${skip.email}: ${
                        skip.reason === "time_off" ? "Time off" : "Booking not ready"
                    }`
            )
            .join("; "),
    }));
    const fallbackColumns = [
        { Header: "Inquiry", accessor: "inquiry", disableSortBy: true },
        { Header: "Coordinator", accessor: "coordinator", disableSortBy: true },
        { Header: "Assigned", accessor: "assigned", disableSortBy: true },
        {
            Header: "Unavailable coordinators",
            accessor: "reason",
            disableSortBy: true,
            Cell: ({ value }: { value: string }) => (
                <span className={styles.detail}>{value}</span>
            ),
        },
    ];

    return (
        <section
            id="coordinator-roster"
            tabIndex={-1}
            className={styles.stack}
            aria-label="Assignment balance"
        >
            <Card classNames={styles.assignmentToolbarCard}>
                <div className={styles.assignmentToolbar}>
                    <label htmlFor="assignment-mode" className={styles.toolbarLabel}>
                        Assignment mode
                    </label>
                    <div className={styles.modeControls}>
                        <div className={styles.modeSelect}>
                            <Select
                                inputId="assignment-mode"
                                aria-label="Assignment mode"
                                value={
                                    modes.find(
                                        (option) => option.value === draftMode
                                    ) || null
                                }
                                options={modes}
                                placeholder={loading ? "Loading…" : "Unavailable"}
                                isClearable={false}
                                isSearchable={false}
                                isDisabled={
                                    loading ||
                                    saving ||
                                    !modeAvailable ||
                                    managementDisabled
                                }
                                onChange={(option) => {
                                    const selected = option as {
                                        value: AssignmentMode;
                                    } | null;
                                    if (selected) {
                                        setModeSelection((previous) => ({
                                            ...previous,
                                            draft: selected.value,
                                        }));
                                        setModeSaved(false);
                                        setModeError("");
                                    }
                                }}
                            />
                        </div>
                        <AssignmentHelp
                            id="assignment-mode-help"
                            label="Assignment mode"
                        >
                            <p>
                                <strong>Round robin:</strong> Share new inquiries
                                equally between coordinators.
                            </p>
                            <p>
                                <strong>Weighted by weekly hours:</strong> More hours
                                means more inquiries. Coordinators on time off are
                                skipped.
                            </p>
                            <p>
                                Returning clients keep their coordinator in both
                                modes. If everyone is skipped in weighted mode, an
                                assignment is still made and flagged for review.
                            </p>
                            <p>Click Save to apply your choice.</p>
                        </AssignmentHelp>
                        <Button
                            disabled={
                                loading ||
                                refreshing ||
                                saving ||
                                managementDisabled ||
                                !modeAvailable ||
                                !modeDirty
                            }
                            aria-label="Save assignment mode"
                            onClick={saveMode}
                        >
                            {saving ? "Saving…" : "Save"}
                        </Button>
                    </div>
                    {!modeAvailable && (
                        <span className="sr-only" role="status">
                            {loading
                                ? "Loading assignment mode…"
                                : "Assignment mode unavailable"}
                        </span>
                    )}
                    {(modeDirty || modeSaved) && !saving && (
                        <span className={styles.helper} role="status">
                            {modeDirty ? "Not saved" : "Assignment mode saved."}
                        </span>
                    )}
                </div>
                {modeError && (
                    <Alert type="danger" contentClassName={styles.alertRow}>
                        <span>{modeError}</span>
                        <Button
                            variant="secondary"
                            disabled={
                                loading || refreshing || saving || managementDisabled
                            }
                            onClick={() =>
                                void (modeAvailable ? saveMode() : load())
                            }
                        >
                            Try again
                        </Button>
                    </Alert>
                )}
            </Card>
            <Card classNames={styles.rosterCard}>
                <div className={styles.sectionHeader}>
                    <div>
                        <h2>Coordinator roster</h2>
                        {hasData && (
                            <p className={styles.helper}>
                                Assignment balance · Last 30 days ·{" "}
                                {moment(period.start, "YYYY-MM-DD").format(
                                    "MMM D, YYYY"
                                )}{" "}
                                –{" "}
                                {moment(period.end, "YYYY-MM-DD").format(
                                    "MMM D, YYYY"
                                )}{" "}
                                · Eastern time
                            </p>
                        )}
                        <p className={styles.helper}>
                            Inquiry tracking began November 3, 2026. Earlier
                            assignments are not included.
                        </p>
                        <p className={styles.helper}>
                            Manage weekly hours and time off for each coordinator.
                        </p>
                    </div>
                    <Button
                        variant="secondary"
                        className={styles.refreshButton}
                        aria-disabled={refreshBlocked}
                        iconLeft={<RefreshIcon />}
                        onClick={refresh}
                    >
                        Refresh balance
                    </Button>
                </div>
                {refreshBusy && hasData && (
                    <p className={styles.helper} role="status">
                        Refreshing assignment balance…
                    </p>
                )}
                {hasData && fallbackEvents.length > 0 && (
                    <Alert type="warning" contentClassName={styles.alertRow}>
                        <div>
                            <strong>
                                {fallbackEvents.length}{" "}
                                {fallbackEvents.length === 1
                                    ? "assignment needs"
                                    : "assignments need"}{" "}
                                review
                            </strong>
                            <p>
                                Everyone was unavailable, so these inquiries were
                                assigned to the usual coordinator pool.
                            </p>
                        </div>
                        <Button
                            variant="secondary"
                            ref={reviewButton}
                            onClick={() => setShowFallbacks(true)}
                        >
                            Review assignments
                        </Button>
                    </Alert>
                )}
                {error && (
                    <Alert type="danger" contentClassName={styles.alertRow}>
                        <span>
                            {error}
                            {hasData ? " Previous results are shown." : ""}
                        </span>
                        <Button
                            variant="secondary"
                            aria-disabled={refreshBlocked}
                            onClick={() => {
                                if (!refreshBlocked) void load();
                            }}
                        >
                            Try again
                        </Button>
                    </Alert>
                )}
                {loading && !hasData ? (
                    <div className={styles.loadingState} role="status">
                        Loading assignment balance…
                    </div>
                ) : hasData ? (
                    rows.length > 0 ? (
                        <Table
                            allowSelect={false}
                            columns={columns}
                            data={rows}
                            stickyLastColumn={Boolean(onManageCoordinator)}
                            footer={
                                <p className={styles.tableNote}>
                                    Actual share includes returning clients; targets
                                    use current hours before time-off/booking
                                    exclusions.
                                </p>
                            }
                        />
                    ) : (
                        <EmptyListState
                            cardClass={styles.emptyRoster}
                            emptyMessage="No assignment activity yet"
                            stateHelperText="Auto-assignable coordinators and new inquiry decisions will appear here."
                        />
                    )
                ) : null}
            </Card>
            <Dialog
                open={showFallbacks}
                onClose={closeReview}
                title="Assignments needing review"
                ariaLabel="Assignments needing review"
                focusManagement={{}}
                showFooter={false}
                wrapperClassName={styles.reviewDialog}
            >
                <p className={styles.helper}>
                    These inquiries were assigned when every coordinator was on time
                    off or not ready for booking.
                </p>
                <Table
                    columns={fallbackColumns}
                    data={fallbackRows}
                    allowSelect={false}
                />
            </Dialog>
        </section>
    );
}
